import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { productCategories } from '../data/productCategories';

const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

const Header = () => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isRentalsEnabled, setIsRentalsEnabled] = useState(false);
  const [isBusinessStudioEnabled, setIsBusinessStudioEnabled] = useState(true);
  const location = useLocation();

  // Fetch rentals and business studio settings to check if enabled
  useEffect(() => {
    const fetchRentalsStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/content/rentals`);
        if (response.ok) {
          const data = await response.json();
          setIsRentalsEnabled(data.enabled || false);
        }
      } catch (error) {
        console.error('Error fetching rentals status:', error);
      }
    };
    
    const fetchBusinessStudioStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/content/business-studio`);
        if (response.ok) {
          const data = await response.json();
          setIsBusinessStudioEnabled(data.enabled !== false); // Default to true
        }
      } catch (error) {
        console.error('Error fetching business studio status:', error);
      }
    };
    
    fetchRentalsStatus();
    fetchBusinessStudioStatus();
  }, []);

  // Mapping from slug to translation key
  const productTranslationKeys = {
    'led-signs': 'products.ledSigns',
    'led-neon-signs': 'products.neonSigns',
    'channel-letter-signs': 'products.channelLetters',
    'acrylic-signs': 'products.acrylicSigns',
    'metal-signs': 'products.metalSigns',
    'lightbox-signs': 'products.lightboxSigns',
    'custom-signs': 'products.customSigns'
  };

  const getProductName = (slug) => {
    const key = productTranslationKeys[slug];
    return key ? t(key) : slug;
  };

  const baseNavLinks = [
    { name: t('header.home'), path: '/' },
    { name: t('header.gallery'), path: '/#gallery' },
    { name: t('header.logoDesign'), path: '/services/logo-design' },
    { name: t('header.brandBoost'), path: '/services/business-enhancement' },
    { name: t('header.businessStudio', 'Business Studio'), path: '/services/business-studio', requiresBusinessStudio: true }
  ];
  
  // Filter out Business Studio if disabled
  const navLinks = baseNavLinks.filter(link => 
    !link.requiresBusinessStudio || isBusinessStudioEnabled
  );

  const handleNavClick = (path) => {
    setIsMobileMenuOpen(false);
    if (path.includes('#')) {
      const [basePath, hash] = path.split('#');
      if (location.pathname === '/' || basePath === '/') {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  return (
    <header 
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        backgroundColor: isDark ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(12px)',
        borderBottom: `2px solid ${isDark ? '#ff00ff' : '#ff1493'}`,
        boxShadow: isDark 
          ? '0 4px 30px rgba(255, 0, 255, 0.3)' 
          : '0 4px 30px rgba(255, 20, 147, 0.2)'
      }}
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 text-xl md:text-2xl font-bold"
            data-testid="header-logo"
          >
            <span 
              style={{ 
                color: isDark ? '#ffffff' : '#0f172a',
                textShadow: isDark ? '0 0 10px rgba(255, 255, 255, 0.5)' : 'none'
              }}
            >
              Custom
            </span>
            <span
              className={isDark ? 'gradient-text-dark' : 'gradient-text-light'}
              style={{
                filter: isDark 
                  ? 'drop-shadow(0 0 8px rgba(255, 0, 255, 0.6))'
                  : 'drop-shadow(0 0 6px rgba(255, 20, 147, 0.5))'
              }}
            >
              Signs
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            {navLinks.slice(0, 1).map((link) => (
              <Link
                key={link.name}
                to={link.path}
                onClick={() => handleNavClick(link.path)}
                className="font-medium transition-all duration-300 hover:scale-105"
                style={{
                  color: isDark ? '#00ffff' : '#0f172a',
                  textShadow: isDark ? '0 0 10px rgba(0, 255, 255, 0.5)' : 'none'
                }}
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}

            {/* Products Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsProductsOpen(true)}
              onMouseLeave={() => setIsProductsOpen(false)}
            >
              <button
                className="flex items-center gap-1 font-medium transition-all duration-300 hover:scale-105"
                style={{
                  color: isDark ? '#00ffff' : '#0f172a',
                  textShadow: isDark ? '0 0 10px rgba(0, 255, 255, 0.5)' : 'none'
                }}
                data-testid="nav-products-dropdown"
              >
                {t('header.products')}
                <ChevronDown 
                  className={`w-4 h-4 transition-transform duration-300 ${isProductsOpen ? 'rotate-180' : ''}`} 
                />
              </button>

              {/* Dropdown Menu */}
              <div 
                className={`absolute top-full left-0 mt-2 w-64 rounded-xl overflow-hidden transition-all duration-300 ${
                  isProductsOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                }`}
                style={{
                  backgroundColor: isDark ? 'rgba(10, 10, 10, 0.98)' : 'rgba(255, 255, 255, 0.98)',
                  border: `2px solid ${isDark ? '#ff00ff' : '#ff1493'}`,
                  boxShadow: isDark 
                    ? '0 10px 40px rgba(255, 0, 255, 0.3)' 
                    : '0 10px 40px rgba(255, 20, 147, 0.2)'
                }}
              >
                {productCategories.map((category, index) => (
                  <Link
                    key={category.id}
                    to={`/products/${category.slug}`}
                    className="block px-4 py-3 transition-all duration-300"
                    style={{
                      borderBottom: index < productCategories.length - 1 
                        ? `1px solid ${isDark ? 'rgba(255, 0, 255, 0.2)' : 'rgba(255, 20, 147, 0.2)'}` 
                        : 'none',
                      color: isDark ? '#ffffff' : '#0f172a',
                      backgroundColor: 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = isDark 
                        ? 'rgba(255, 0, 255, 0.15)' 
                        : 'rgba(255, 20, 147, 0.1)';
                      e.currentTarget.style.color = isDark ? '#ff00ff' : '#ff1493';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = isDark ? '#ffffff' : '#0f172a';
                    }}
                    data-testid={`nav-product-${category.slug}`}
                  >
                    {getProductName(category.slug)}
                  </Link>
                ))}
              </div>
            </div>

            {navLinks.slice(1).map((link) => (
              <Link
                key={link.name}
                to={link.path}
                onClick={() => handleNavClick(link.path)}
                className="font-medium transition-all duration-300 hover:scale-105"
                style={{
                  color: isDark ? '#00ffff' : '#0f172a',
                  textShadow: isDark ? '0 0 10px rgba(0, 255, 255, 0.5)' : 'none'
                }}
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Right Side - Sign Rentals + CTA */}
          <div className="hidden md:flex items-center gap-4">
            {/* Sign Rentals Link - Only show if enabled */}
            {isRentalsEnabled && (
              <Link
                to="/services/sign-rentals"
                className="text-center transition-all duration-300 hover:scale-105"
                data-testid="nav-sign-rentals"
              >
                <span 
                  className="block font-medium"
                  style={{
                    color: isDark ? '#00ffff' : '#0f172a',
                    textShadow: isDark ? '0 0 10px rgba(0, 255, 255, 0.5)' : 'none'
                  }}
                >
                  {t('header.signRentals', 'Sign Rentals')}
                </span>
                <span 
                  className="block text-xs font-normal"
                  style={{ 
                    color: isDark ? '#ff00ff' : '#ff1493',
                    textShadow: isDark ? '0 0 8px rgba(255, 0, 255, 0.6)' : 'none'
                  }}
                >
                  {t('header.signRentalsSubtitle', 'Weddings & More')}
                </span>
              </Link>
            )}

            {/* CTA Button */}
            <Link
              to="/#quote"
              onClick={() => handleNavClick('/#quote')}
              className="px-6 py-2 rounded-full font-bold transition-all duration-300 hover:scale-105"
              style={{
                background: isDark 
                  ? 'linear-gradient(135deg, #ff00ff 0%, #ff0080 100%)'
                  : 'linear-gradient(135deg, #ff1493 0%, #ff0080 100%)',
                color: '#ffffff',
                boxShadow: isDark 
                  ? '0 4px 20px rgba(255, 0, 255, 0.5)' 
                  : '0 4px 20px rgba(255, 20, 147, 0.4)'
              }}
              data-testid="header-cta"
            >
              {t('header.getQuote')}
            </Link>
          </div>

          {/* Mobile Menu Button with flashing animation */}
          <button
            className="md:hidden p-2 rounded-lg transition-colors duration-300 flex items-center gap-1"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            style={{
              color: isDark ? '#ff00ff' : '#ff1493'
            }}
            data-testid="mobile-menu-button"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <div className="relative w-12 h-6 overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center animate-menu-flash">
                  <Menu className="w-6 h-6" />
                </div>
                <div className="absolute inset-0 flex items-center justify-center animate-menu-flash-text">
                  <span className="text-xs font-bold uppercase tracking-wider">Menu</span>
                </div>
              </div>
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        <div 
          className={`md:hidden overflow-hidden transition-all duration-300 ${
            isMobileMenuOpen ? 'max-h-screen pb-4' : 'max-h-0'
          }`}
        >
          <nav className="flex flex-col gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                onClick={() => handleNavClick(link.path)}
                className="px-4 py-3 rounded-lg font-medium transition-all duration-300"
                style={{
                  color: isDark ? '#00ffff' : '#0f172a',
                  backgroundColor: isDark ? 'rgba(0, 255, 255, 0.1)' : 'rgba(255, 20, 147, 0.05)'
                }}
              >
                {link.name}
              </Link>
            ))}

            {/* Mobile Sign Rentals - Right after main links */}
            {isRentalsEnabled && (
              <Link
                to="/services/sign-rentals"
                onClick={() => setIsMobileMenuOpen(false)}
                className="px-4 py-3 rounded-lg font-medium transition-all duration-300 flex items-center justify-between"
                style={{
                  color: isDark ? '#00ffff' : '#0f172a',
                  backgroundColor: isDark ? 'rgba(0, 255, 255, 0.15)' : 'rgba(14, 165, 233, 0.1)',
                  border: `1px solid ${isDark ? '#00ffff' : '#0ea5e9'}`
                }}
              >
                <span>{t('header.signRentals', 'Sign Rentals')}</span>
                <span 
                  className="text-xs px-2 py-1 rounded-full"
                  style={{ 
                    backgroundColor: isDark ? '#ff00ff' : '#ff1493',
                    color: '#ffffff'
                  }}
                >
                  {t('header.signRentalsSubtitle', 'Weddings & More')}
                </span>
              </Link>
            )}

            {/* Mobile Products Submenu */}
            <div className="px-4 py-2">
              <span 
                className="text-sm font-semibold mb-2 block"
                style={{ color: isDark ? '#ff00ff' : '#ff1493' }}
              >
                {t('header.products')}
              </span>
              <div className="flex flex-col gap-1 ml-2">
                {productCategories.map((category) => (
                  <Link
                    key={category.id}
                    to={`/products/${category.slug}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="px-3 py-2 rounded-lg text-sm transition-all duration-300"
                    style={{
                      color: isDark ? '#ffffff' : '#334155',
                      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'
                    }}
                  >
                    {getProductName(category.slug)}
                  </Link>
                ))}
              </div>
            </div>

            <Link
              to="/#quote"
              onClick={() => handleNavClick('/#quote')}
              className="mx-4 mt-2 px-6 py-3 rounded-full font-bold text-center transition-all duration-300"
              style={{
                background: isDark 
                  ? 'linear-gradient(135deg, #ff00ff 0%, #ff0080 100%)'
                  : 'linear-gradient(135deg, #ff1493 0%, #ff0080 100%)',
                color: '#ffffff',
                boxShadow: isDark 
                  ? '0 4px 20px rgba(255, 0, 255, 0.5)' 
                  : '0 4px 20px rgba(255, 20, 147, 0.4)'
              }}
            >
              Get Quote
            </Link>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
